import { useState } from 'react';
import { Check, ChevronDown, Filter, ListFilter } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { cn } from '@/shared/lib/cn';

// Cierre de Caja rows are hard-hidden from the gallery in TesoreriaPage
// (they're prior-period bookkeeping) and Cierres now have a dedicated
// browser under Reporting → Cierres de Caja, so the user-facing filter
// drops that option. The Set type below stays narrow on purpose: if a
// future tipo is added it must be added to TIPO_OPTIONS too.
export type TesoreriaFilterValue = {
  tiposMovimiento: Set<'Ingreso' | 'Egreso' | 'Apertura de Caja'>;
};

export const DEFAULT_TESORERIA_FILTER: TesoreriaFilterValue = {
  tiposMovimiento: new Set(['Ingreso', 'Egreso', 'Apertura de Caja']),
};

type TipoMovimiento =
  TesoreriaFilterValue['tiposMovimiento'] extends Set<infer U> ? U : never;

const TIPO_OPTIONS: Array<{ value: TipoMovimiento; label: string }> = [
  { value: 'Ingreso', label: 'Ingresos' },
  { value: 'Egreso', label: 'Egresos' },
  { value: 'Apertura de Caja', label: 'Aperturas' },
];

export function FiltrarPopover({
  value,
  onChange,
  onReset,
  active,
}: {
  value: TesoreriaFilterValue;
  onChange: (next: TesoreriaFilterValue) => void;
  onReset: () => void;
  active: boolean;
}) {
  const [open, setOpen] = useState(false);

  const toggleTipo = (t: TipoMovimiento) => {
    const next = new Set(value.tiposMovimiento);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    onChange({ ...value, tiposMovimiento: next });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('h-12 gap-2', active && 'border-foreground/60')}
        >
          <Filter className="h-4 w-4" aria-hidden />
          Filtrar
          {active && <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />}
          <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 p-3">
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Tipo
          </p>
          <div className="flex flex-col gap-1">
            {TIPO_OPTIONS.map((t) => (
              <FilterRadio
                key={t.value}
                label={t.label}
                checked={value.tiposMovimiento.has(t.value)}
                onSelect={() => toggleTipo(t.value)}
              />
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onReset}>
            Reiniciar
          </Button>
          <Button size="sm" onClick={() => setOpen(false)}>
            Listo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterRadio({
  label,
  checked,
  onSelect,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition-colors',
        checked
          ? 'border-foreground bg-foreground/5 text-foreground'
          : 'border-border text-muted-foreground hover:bg-secondary',
      )}
    >
      <span>{label}</span>
      {checked && <Check className="h-4 w-4 text-emerald-600" aria-hidden />}
    </button>
  );
}

export function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}
